'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { formatCurrency } from '@coingecko/cryptoformat'
import { Calendar, Download, PieChart, Store, TrendingDown, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'
import { Transaction, TransactionStatus, TransactionType } from '../hooks/useTransactionHistory'

interface SpendingInsightsProps {
  transactions: Transaction[]
  onExport: () => void
}

interface MonthlyData {
  month: string
  total: number
  count: number
  categories: Record<string, number>
}

interface CategoryData {
  name: string
  amount: number
  percentage: number
  count: number
}

interface MerchantData {
  name: string
  amount: number
  count: number
  icon?: string
}

export function SpendingInsights({ transactions, onExport }: SpendingInsightsProps) {
  const insights = useMemo(() => {
    const validTransactions = transactions.filter(
      (t) =>
        (t.type === TransactionType.SPEND || t.type === TransactionType.PURCHASE) &&
        (t.settlement_status === 'healthy' ||
          t.status === TransactionStatus.POSTED ||
          t.status === TransactionStatus.COMPLETED),
    )

    if (validTransactions.length === 0) {
      return null
    }

    const totalSpent = validTransactions.reduce((sum, t) => sum + t.amount, 0)
    const avgTransaction = totalSpent / validTransactions.length
    const transactionCount = validTransactions.length

    const monthlyMap = new Map<string, MonthlyData>()
    for (const t of validTransactions) {
      const date = new Date(t.created_at)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

      const existing = monthlyMap.get(monthKey)
      if (existing) {
        existing.total += t.amount
        existing.count += 1
        const category = t.merchant.category || 'Other'
        existing.categories[category] = (existing.categories[category] || 0) + t.amount
      } else {
        const categories: Record<string, number> = {}
        const category = t.merchant.category || 'Other'
        categories[category] = t.amount
        monthlyMap.set(monthKey, {
          month: monthLabel,
          total: t.amount,
          count: 1,
          categories,
        })
      }
    }
    const monthlyData = Array.from(monthlyMap.values()).sort((a, b) => b.total - a.total)

    const categoryMap = new Map<string, { amount: number; count: number }>()
    for (const t of validTransactions) {
      const category = t.merchant.category || 'Other'
      const existing = categoryMap.get(category)
      if (existing) {
        existing.amount += t.amount
        existing.count += 1
      } else {
        categoryMap.set(category, { amount: t.amount, count: 1 })
      }
    }
    const categories: CategoryData[] = Array.from(categoryMap.entries())
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        percentage: (data.amount / totalSpent) * 100,
        count: data.count,
      }))
      .sort((a, b) => b.amount - a.amount)

    const merchantMap = new Map<string, { amount: number; count: number; icon?: string }>()
    for (const t of validTransactions) {
      const name = t.merchant.name
      const existing = merchantMap.get(name)
      if (existing) {
        existing.amount += t.amount
        existing.count += 1
      } else {
        merchantMap.set(name, { amount: t.amount, count: 1, icon: t.merchant.icon })
      }
    }
    const topMerchants: MerchantData[] = Array.from(merchantMap.entries())
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        count: data.count,
        icon: data.icon,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)

    const sortedMonths = Array.from(monthlyMap.entries()).sort((a, b) => a[0].localeCompare(b[0]))
    let trend: { direction: 'up' | 'down' | 'stable'; percentage: number } | null = null
    if (sortedMonths.length >= 2) {
      const lastMonthData = sortedMonths[sortedMonths.length - 1]?.[1]
      const prevMonthData = sortedMonths[sortedMonths.length - 2]?.[1]
      if (lastMonthData && prevMonthData) {
        const change = lastMonthData.total - prevMonthData.total
        const percentage = (change / prevMonthData.total) * 100
        trend = {
          direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
          percentage: Math.abs(percentage),
        }
      }
    }

    return {
      totalSpent,
      avgTransaction,
      transactionCount,
      monthlyData,
      categories,
      topMerchants,
      trend,
    }
  }, [transactions])

  if (!insights) {
    return (
      <Panel.Wrapper>
        <Panel.Content className="p-6">
          <div className="flex items-center justify-between">
            <Typography variant="h4" className="font-semibold">
              Spending Insights
            </Typography>
            <Button variant="outline" size="sm" onClick={onExport} className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
          <Typography variant="p" className="mt-4 text-muted-foreground">
            No spending data available yet. Make some purchases to see insights.
          </Typography>
        </Panel.Content>
      </Panel.Wrapper>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography variant="h4" className="font-semibold">
          Spending Insights
        </Typography>
        <Button variant="outline" size="sm" onClick={onExport} className="gap-2">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Panel.Wrapper>
          <Panel.Content className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <Typography variant="p" className="text-muted-foreground text-xs">
                Total Spent
              </Typography>
            </div>
            <Typography variant="h3" className="mt-1 font-semibold">
              {formatCurrency(insights.totalSpent / 100, 'USD', 'en-US')}
            </Typography>
          </Panel.Content>
        </Panel.Wrapper>

        <Panel.Wrapper>
          <Panel.Content className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Typography variant="p" className="text-muted-foreground text-xs">
                Transactions
              </Typography>
            </div>
            <Typography variant="h3" className="mt-1 font-semibold">
              {insights.transactionCount}
            </Typography>
            <Typography variant="p" className="text-muted-foreground text-xs">
              Avg: {formatCurrency(insights.avgTransaction / 100, 'USD', 'en-US')}
            </Typography>
          </Panel.Content>
        </Panel.Wrapper>

        <Panel.Wrapper>
          <Panel.Content className="p-4">
            <div className="flex items-center gap-2">
              <PieChart className="h-4 w-4 text-muted-foreground" />
              <Typography variant="p" className="text-muted-foreground text-xs">
                Trend
              </Typography>
            </div>
            {insights.trend ? (
              <div className="mt-1 flex items-center gap-1">
                {insights.trend.direction === 'up' ? (
                  <TrendingUp className="h-5 w-5 text-red-400" />
                ) : insights.trend.direction === 'down' ? (
                  <TrendingDown className="h-5 w-5 text-green-400" />
                ) : null}
                <Typography
                  variant="h3"
                  className={cn('font-semibold', insights.trend.direction === 'up' ? 'text-red-400' : 'text-green-400')}
                >
                  {insights.trend.percentage.toFixed(1)}%
                </Typography>
              </div>
            ) : (
              <Typography variant="p" className="text-muted-foreground text-xs">
                Need more data
              </Typography>
            )}
            <Typography variant="p" className="text-muted-foreground text-xs">
              vs last month
            </Typography>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      {/* Category breakdown */}
      {insights.categories.length > 0 && (
        <Panel.Wrapper>
          <Panel.Content className="p-4">
            <Typography variant="p" className="mb-3 font-medium text-sm">
              Top Categories
            </Typography>
            <div className="space-y-2">
              {insights.categories.slice(0, 5).map((category) => (
                <div key={category.name} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <Typography variant="p" className="text-sm">
                        {category.name}
                      </Typography>
                      <Typography variant="p" className="font-medium text-sm">
                        {formatCurrency(category.amount / 100, 'USD', 'en-US')}
                      </Typography>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-gradient-yellow to-gradient-green"
                        style={{ width: `${category.percentage}%` }}
                      />
                    </div>
                  </div>
                  <Typography variant="p" className="w-12 text-right text-muted-foreground text-xs">
                    {category.percentage.toFixed(0)}%
                  </Typography>
                </div>
              ))}
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      )}

      {/* Top merchants */}
      {insights.topMerchants.length > 0 && (
        <Panel.Wrapper>
          <Panel.Content className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <Store className="h-4 w-4 text-muted-foreground" />
              <Typography variant="p" className="font-medium text-sm">
                Top Merchants
              </Typography>
            </div>
            <div className="space-y-2">
              {insights.topMerchants.map((merchant) => (
                <div
                  key={merchant.name}
                  className="flex items-center justify-between rounded-lg border border-border/50 p-3"
                >
                  <div className="flex items-center gap-3">
                    {merchant.icon ? (
                      <img src={merchant.icon} alt={merchant.name} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary">
                        <Store className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <Typography variant="p" className="font-medium text-sm">
                        {merchant.name}
                      </Typography>
                      <Typography variant="p" className="text-muted-foreground text-xs">
                        {merchant.count} transactions
                      </Typography>
                    </div>
                  </div>
                  <Typography variant="p" className="font-medium text-sm">
                    {formatCurrency(merchant.amount / 100, 'USD', 'en-US')}
                  </Typography>
                </div>
              ))}
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      )}
    </div>
  )
}
